import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T, { specularRing, specularHost, specularRingReduced } from '../../home-new/tokens'
import Reveal from '../../home-new/Reveal'

// Hero for /service?new. banner.png (Banner, 2000×722) IS the full hero banner —
// it already carries the soft light-blue background + decorative circles on the
// left and the woman + laptop + SHIPPOP dashboard scene on the right. So it's
// shown FULL-WIDTH edge-to-edge (width:100%, natural aspect) and the heading +
// subheading + CTAs overlay the banner's empty light LEFT area — matching the
// design (same pattern as the /new homepage banner), not a contained image on a
// separate gradient. On mobile it stacks: text on the light gradient, banner
// full-width below.
const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    background: T.gradient.hero,
    // Bleed the banner UP behind the fixed HeaderNew (cancel NewShell's main
    // paddingTop:92). NO top padding: the in-flow bannerImg then sits flush at
    // the hero's top edge so the scene shows BEHIND the floating header (no
    // plain gradient strip), matching the /new & /business heroes. The text
    // `overlay` (position:absolute, top:120) still clears the header.
    [theme.breakpoints.up('md')]: {
      marginTop: -92,
    },
    // Mobile: same treatment as /business HeroBiz — banner as a full cover
    // background with the copy on a frosted panel over it.
    [theme.breakpoints.down('md')]: {
      minHeight: 'clamp(600px, 132vw, 760px)',
    },
  },
  // Full-width banner. Its natural aspect (~2.77:1) drives the hero height on
  // desktop so the whole scene shows uncropped; the overlay is centered on top.
  bannerImg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      // The woman's face is at ~52% of the (very wide 3.34:1) banner — frame
      // there so the portrait mobile crop centres on her, not the empty left.
      objectPosition: '52% center',
      marginTop: 0,
      zIndex: 0,
    },
  },
  // Desktop: absolute layer centered over the banner. Mobile: normal flow above
  // the banner (text first, image below).
  overlay: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      // Align with the banner (below the paddingTop strip), not the section
      // top, so the copy centers over the scene rather than the header strip.
      top: 120,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      zIndex: 2,
      pointerEvents: 'none',
    },
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'flex-end',
      zIndex: 2,
      paddingTop: 100,
      paddingBottom: 26,
      pointerEvents: 'none',
    },
  },
  overlayInner: { width: '100%' },
  copy: {
    maxWidth: 560,
    pointerEvents: 'auto',
    [theme.breakpoints.down('lg')]: { maxWidth: 460 },
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      textAlign: 'center',
      background: 'rgba(255,255,255,0.80)',
      WebkitBackdropFilter: 'blur(10px)',
      backdropFilter: 'blur(10px)',
      borderRadius: 24,
      padding: '26px 22px 28px',
      boxShadow: '0 16px 44px rgba(0,102,204,0.16)',
      border: '1px solid rgba(255,255,255,0.6)',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 54,
    fontWeight: 700,
    lineHeight: 1.28,
    color: T.color.ink,
    margin: '0 0 20px',
    '& span': {
      display: 'inline-block',
      color: T.color.brand,
      backgroundImage:
        'linear-gradient(to right, #0066CC, #009EFF, #33C9FF, #66D9FF, #33C9FF, #009EFF, #0066CC)',
      backgroundSize: '300% 100%',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: '$flow 8s linear infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    [theme.breakpoints.down('lg')]: { fontSize: 46 },
    [theme.breakpoints.down('md')]: { fontSize: 40 },
    [theme.breakpoints.down('sm')]: { fontSize: 30 },
  },
  '@keyframes flow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  sub: {
    fontFamily: T.font.body,
    fontSize: 17,
    lineHeight: 1.75,
    color: T.color.inkSoft,
    margin: '0 0 32px',
    maxWidth: 500,
    [theme.breakpoints.down('md')]: { margin: '0 auto 28px' },
    [theme.breakpoints.down('sm')]: { fontSize: 15 },
  },
  ctaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    [theme.breakpoints.down('md')]: { justifyContent: 'center' },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '12px',
      '& > a': { width: '100%' },
    },
  },
  ctaDark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    padding: '0 34px',
    borderRadius: T.radius.pill,
    background: T.color.ink,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 18,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: T.shadow.md,
    '&:hover': { transform: 'translateY(-2px)', color: T.color.onBrand, boxShadow: T.shadow.lg },
    [theme.breakpoints.down('sm')]: { minHeight: 48, fontSize: 16, padding: '0 26px' },
  },
  ctaPrimary: {
    ...specularHost,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    padding: '0 34px',
    borderRadius: T.radius.pill,
    background: T.gradient.blue,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 18,
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: '0 8px 24px rgba(0,102,204,0.28)',
    '&::before': specularRing(T.ring.blue, '1px'),
    '&:hover': { transform: 'translateY(-2px)', color: T.color.onBrand },
    '@media (prefers-reduced-motion: reduce)': specularRingReduced,
    [theme.breakpoints.down('sm')]: { minHeight: 48, fontSize: 16, padding: '0 26px' },
  },
}))

const ServiceHero = () => {
  const classes = useStyles()
  const { t } = useTranslation('services')
  return (
    <section className={classes.root}>
      <div className={classes.overlay}>
        <Container
          maxWidth={false}
          style={{ maxWidth: T.container }}
          className={classes.overlayInner}
        >
          <div className={classes.copy}>
            <Reveal direction="left">
              <h1 className={classes.heading}>
                {t('new.hero.title-1')}
                <br />
                <span>{t('new.hero.title-2')}</span>
              </h1>
            </Reveal>
            <Reveal direction="left" delay={0.1}>
              <p className={classes.sub}>{t('new.hero.sub')}</p>
            </Reveal>
            <Reveal direction="up" delay={0.18}>
              <div className={classes.ctaRow}>
                <a href="/checkprice" className={classes.ctaDark}>
                  {t('new.hero.cta-checkprice')}
                </a>
                <a href="/register" className={classes.ctaPrimary}>
                  {t('new.hero.cta-start')}
                </a>
              </div>
            </Reveal>
          </div>
        </Container>
      </div>
      <picture style={{ display: 'contents' }}>
        <source
          srcSet="/static/img/service-new/v2/hero/banner.webp"
          type="image/webp"
        />
        <img
          className={classes.bannerImg}
          src="/static/img/service-new/v2/hero/banner.png"
          alt={t('new.hero.banner-alt')}
          width={2000}
          height={722}
          fetchpriority="high"
          decoding="async"
        />
      </picture>
    </section>
  )
}

export default ServiceHero
